import React, { CSSProperties, ReactElement, ReactNode } from "react";
import styles from "./styles.module.scss";
import {
    Variables,
    Section as SectionType,
    SectionColumnGroup,
} from "@page-builder/ts/types";
import { convertToBreakpointVariables } from "@page-builder/utils";
import dynamic from "next/dynamic";
import { useDomainContext } from "@hooks/useDomainContext";
import { resolveComponentProps } from "@page-builder/utils/resolveDataSource";

import { Component } from "@page-builder/ts/generated";
import MainSectionBgPreload from "@page-builder/components/helpers/MainSectionBgPreload";
import { TAffiliateOfferList } from "@page-builder/ts/generated";
const Form = dynamic(() => import("../components/form"));
const FeatureList = dynamic(() => import("../components/featureList"));
const AffiliateOfferList = dynamic(
    () => import("../components/affiliateOfferList"),
);
const TextBlock = dynamic(() => import("@page-builder/components/textBlock"));
const TermsAndConditions = dynamic(
    () => import("@components/shared/legal/termsAndConditions"),
);
const CcpaPrivacyNotice = dynamic(
    () => import("@components/shared/legal/ccpaPrivacyNotice"),
);
const Blog = dynamic(() => import("@page-builder/components/blog"));
const ContactUs = dynamic(() => import("@page-builder/components/contactUs"), {
    ssr: false,
});
const ScrollToTop = dynamic(
    () => import("@page-builder/components/scrollToTop"),
    { ssr: false },
);
const ContentWithImage = dynamic(
    () => import("@page-builder/components/contentWithImage"),
);
const Claims = dynamic(() => import("@page-builder/components/Claims"), {
    ssr: false,
});
const Faq = dynamic(() => import("@page-builder/components/faq"), {
    ssr: false,
});
const Accordion = dynamic(() => import("@page-builder/components/accordion"), {
    ssr: false,
});
const FeatureListWithImage = dynamic(
    () => import("@page-builder/components/featureListWithImage"),
);
const StandAloneImage = dynamic(
    () => import("@page-builder/components/standAloneImage"),
);
const LookupsList = dynamic(
    () => import("@page-builder/components/lookupsList"),
);
const PrivacyPolicy = dynamic(
    () => import("@components/shared/legal/privacyPolicy"),
);
const CategoryList = dynamic(
    () => import("@page-builder/components/categoryList"),
);
const ThankYou = dynamic(() => import("@page-builder/components/thankYou"));
const Button = dynamic(() => import("@page-builder/components/button"));
const ButtonCategoriesListModal = dynamic(
    () => import("@page-builder/components/buttonCategoriesListModal"),
);
const UsStatesMap = dynamic(
    () => import("@page-builder/components/usStatesMap"),
);
const Carousel = dynamic(() => import("@page-builder/components/carousel"));
const StatesPins = dynamic(() => import("@page-builder/components/statesPins"));
const CrossSellingDomains = dynamic(
    () => import("@page-builder/components/crossSellingDomains"),
);
const ContactUsForm = dynamic(
    () => import("@page-builder/components/contactUsForm"),
);
const FeaturedServiceAreas = dynamic(
    () => import("@page-builder/components/featuredServiceAreas"),
);
const TableOfContents = dynamic(
    () => import("@page-builder/components/tableOfContents"),
);
const Tabs = dynamic(() => import("@page-builder/components/tabs"));

const InteractiveImage = dynamic(
    () => import("@page-builder/components/InteractiveImage"),
);
const PostsGrid = dynamic(() => import("@page-builder/components/postsGrid"));
const Table = dynamic(() => import("@page-builder/components/table"));
const availableComponents = {
    Form,
    FeatureList,
    AffiliateOfferList,
    TextBlock,
    PrivacyPolicy,
    TermsAndConditions,
    CcpaPrivacyNotice,
    Blog,
    ContactUs,
    ScrollToTop,
    ContentWithImage,
    Claims,
    Faq,
    FeatureListWithImage,
    StandAloneImage,
    LookupsList,
    CategoryList,
    ThankYou,
    Button,
    ButtonCategoriesListModal,
    Accordion,
    UsStatesMap,
    Carousel,
    StatesPins,
    CrossSellingDomains,
    ContactUsForm,
    FeaturedServiceAreas,
    TableOfContents,
    Tabs,
    InteractiveImage,
    PostsGrid,
    Table,
};

// ─── Grouping helpers ────────────────────────────────────────────────────────

/** Returns the active column groups when grouping is enabled, otherwise an empty array. */
function getActiveGroups(props?: SectionType["props"]): SectionColumnGroup[] {
    return props?.enableGroups && props.groups?.length ? props.groups : [];
}

/** Extracts the `columnGroup` name from a component's props, or undefined if absent. */
function getColumnGroupName(component: Component): string | undefined {
    const groupName = (component.props as Record<string, unknown>)?.columnGroup;
    return typeof groupName === "string" ? groupName : undefined;
}

/**
 * Distributes rendered component nodes into named group buckets.
 * Components without a matching group name fall into `ungrouped`.
 */
function bucketComponentsByGroup(
    components: Component[],
    validGroupNames: Set<string>,
    renderFn: (component: Component, index: number) => ReactNode,
): { buckets: Map<string, ReactNode[]>; ungrouped: ReactNode[] } {
    const buckets = new Map<string, ReactNode[]>();
    const ungrouped: ReactNode[] = [];

    components.forEach((component, index) => {
        const groupName = getColumnGroupName(component);
        const node = renderFn(component, index);

        if (groupName && validGroupNames.has(groupName)) {
            const bucket = buckets.get(groupName) ?? [];
            bucket.push(node);
            buckets.set(groupName, bucket);
        } else {
            ungrouped.push(node);
        }
    });

    return { buckets, ungrouped };
}

// ─────────────────────────────────────────────────────────────────────────────

export default function Section({
    variables = {},
    components,
    props = {},
    sectionIndex,
    classIdentifier,
}: {
    variables?: Variables;
    components: Component[];
    props?: SectionType["props"];
    sectionIndex: number;
    classIdentifier?: string;
}): ReactElement {
    const { apiCallLoading, angiWidgetDetails, pageBuilderBlogPost, domain } =
        useDomainContext();

    const hasFormOrClaims = components.some(
        (i) => i.id === "Form" || i.id === "Claims",
    );

    // Filter components dynamically based on angiWidget status
    // This will recalculate whenever angiWidgetDetails.showWidget changes
    // angiWidgetDetails.showWidget === undefined case is needed to avoid hiding offers on initial render before angiWidgetDetails is set)

    const filteredComponents = components.filter((component) => {
        // Hide AffiliateOfferList if it has thankYou zone and widget is undefined or true
        if (
            component.id === "AffiliateOfferList" &&
            (component as TAffiliateOfferList).props?.zone === "thankYou" &&
            (component as TAffiliateOfferList).props?.thumbtackFormat ===
                true &&
            (angiWidgetDetails.showWidget === undefined ||
                angiWidgetDetails.showWidget === true)
        ) {
            return false;
        }
        return true;
    });
    // If section only had one component and it was filtered out, don't render the section at all
    if (components.length === 1 && filteredComponents.length === 0) {
        return <></>;
    }

    const renderComponent = (component: Component, componentIndex: number) => {
        const ResolvedComponent = availableComponents[component.id];

        if (!ResolvedComponent) {
            return null;
        }

        return (
            <div
                style={{
                    ...convertToBreakpointVariables(
                        { ...component.variables },
                        component.id === "Tabs" ? "tabs" : undefined,
                    ),
                }}
                key={componentIndex}
                className={`${styles.component} ${
                    component.classIdentifier ? component.classIdentifier : ""
                }`}
                id={`${component.id}-${componentIndex}-${sectionIndex}`}
            >
                <ResolvedComponent
                    props={
                        resolveComponentProps(
                            (component.props || {}) as Record<string, unknown>,
                            pageBuilderBlogPost,
                            component.id,
                            domain,
                        ) as typeof component.props
                    }
                />
            </div>
        );
    };

    // Grouping places components into named column groups within the section grid.
    // Groups are only active when `enableGroups` is set and at least one group is defined.
    const activeGroups = getActiveGroups(props);

    let renderedChildren: ReactNode;
    if (activeGroups.length === 0) {
        renderedChildren = filteredComponents.map((component, index) =>
            renderComponent(component, index),
        );
    } else {
        const validGroupNames = new Set(activeGroups.map((g) => g.name));
        const { buckets, ungrouped } = bucketComponentsByGroup(
            filteredComponents,
            validGroupNames,
            renderComponent,
        );

        const columns = activeGroups
            .filter((group) => buckets.has(group.name))
            .map((group) => (
                <div
                    key={`group-${group.name}`}
                    className={styles.columnGroup}
                    style={
                        convertToBreakpointVariables({
                            groupColumnStart: group.columnStart,
                            groupColumnSpan: group.columnSpan,
                            groupGap: group.gap,
                        } as Variables) as CSSProperties
                    }
                >
                    {buckets.get(group.name)}
                </div>
            ));

        renderedChildren = [...columns, ...ungrouped];
    }

    return (
        <section
            style={
                {
                    ...convertToBreakpointVariables(variables),
                    "--formBackground-sm": hasFormOrClaims
                        ? "var(--global-form-general-containing-section-background-color)"
                        : undefined,
                } as CSSProperties
            }
            className={`${styles.section} ${
                classIdentifier ? classIdentifier : ""
            } ${
                apiCallLoading && hasFormOrClaims ? styles.sectionOverlay : ""
            }`}
            id={`section-${sectionIndex}`}
        >
            {sectionIndex === 0 && (
                <MainSectionBgPreload variables={variables} />
            )}
            {renderedChildren}
        </section>
    );
}
